import { createStyles, withStyles } from '@mui/styles'
import { muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import React, { useEffect, useState } from 'react'
import { ColumnDescriptor, Select, TableItem, TextField } from '../../common/customComponents'
import { t } from '../../../infrastructure/i18nextHelper'
import * as api from '../../../infrastructure/api'
import guid, { Guid } from '../../../infrastructure/guid'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import { MeanOfTransportation } from '../../stock/stockModels'
import { dealApi } from '../../deals/dealApis'
import { DealTypes } from '../../deals/dealModels'
import { PricingFormula, getFormulaTypes } from './pricingPeriod'
import { hasFeature } from '../../../infrastructure/feature'

function SupplyDealDefaultValue({ classes }: MuiProps) {
    let [items, setItems] = useState<SupplyDealDefaultValueItem[]>([emptyItem()])
    let [companys, setCompanys] = useState<Company[]>([])
    let [counterpartys, setCounterpartys] = useState<Counterparty[]>([])
    let [sites, setSites] = useState<Site[]>([])
    let [products, setProducts] = useState<Product[]>([])
    let [dutyStatuss, setDutyStatuss] = useState<DutyStatus[]>([])
    let [meanOfTransportations, setMeanOfTransportations] = useState<MeanOfTransportation[]>([])
    let [priceIndexs, setPriceIndexs] = useState<PriceIndex[]>([])
    let [quantityUnitPrices, setQuantityUnitPrices] = useState<QuantityUnitPrice[]>([])
    let [pricingPeriodRules, setPricingPeriodRules] = useState<PricingPeriodRule[]>([])
    let [filters, setFilters] = useState<ItemFilters>(noFilter)

    let excelGenerator = ExcelGeneratorContainer.useContainer()
    let isManager = hasClaim(Claims.MasterdataSupplyDealDefaultValueManager)
    let tBase = 'admin.masterdata.supplyDealDefaultValue.'

    useEffect(() => {
        loadReferentials()
        load()
    }, [])

    let load = async () => {
        setItems(await api.get<SupplyDealDefaultValueItem[]>('masterdata/supplyDealDefaultValue'))
    }

    let loadReferentials = async () => {
        let referentials = await api.get<ReferenceDto>('masterdata/supplyDealDefaultValue/referential')
        setCompanys(referentials.companys)
        setCounterpartys(referentials.counterpartys)
        setSites(referentials.sites)
        setProducts(referentials.products)
        setDutyStatuss(referentials.dutyStatuss)
        setMeanOfTransportations(await dealApi.getMot())
        setPriceIndexs(referentials.priceIndexs)
        setQuantityUnitPrices(referentials.quantityUnitPrices)
        setPricingPeriodRules(referentials.pricingPeriodRules)
    }

    let onSaveHandler = async (item: SupplyDealDefaultValueItem) => {
        let { id, ...rest } = item
        let allNullOrEmpty = Object.values(rest).every(v => v === null || v === '')
        if (allNullOrEmpty) return false
        await api.post<SupplyDealDefaultValueItem>('masterdata/supplyDealDefaultValue', item)
        await load()
        return true
    }

    let onDeleteHandler = async (ids: string[]) => {
        await api.del('masterdata/supplyDealDefaultValue', { ids: ids })
        await load()
        return true
    }

    let onExportExcelHander = () => {
        excelGenerator.generate({
            filename: 'SupplyDealDefaultValues.xlsx',
            sheets: [{ name: 'SupplyDealDefaultValues', lines: createExcelLines(getItems(), columns) }]
        })
    }

    let columns: ColumnDescriptor<TableItem<MasterDataItem<SupplyDealDefaultValueItem>>>[] = [
        {
            name: t(tBase + 'company'), value: (item) => item.company,
            columnFilter: {
                value: filters.company ?? '',
                onChange: (value: string) => setFilters({ ...filters, company: value })
            }
        },
        {
            name: t(tBase + 'movementType'), value: (item) => item.movementType,
            columnFilter: {
                value: filters.movementType ?? '',
                onChange: (value: string) => setFilters({ ...filters, movementType: value })
            }
        },
        {
            name: t(tBase + 'dutyStatus'), value: (item) => item.dutyStatus,
            columnFilter: {
                value: filters.dutyStatus ?? '',
                onChange: (value: string) => setFilters({ ...filters, dutyStatus: value })
            }
        },
        {
            name: t(tBase + 'meanOfTransportation'), value: (item) => item.meanOfTransportation,
            columnFilter: {
                value: filters.meanOfTransportation ?? '',
                onChange: (value: string) => setFilters({ ...filters, meanOfTransportation: value })
            }
        },
        {
            name: t(tBase + 'counterparty'),
            value: (item) => counterpartys.find(c => c.id === item.counterpartyId)?.name ?? '',
            columnFilter: {
                value: filters.counterparty ?? '',
                onChange: (value: string) => setFilters({ ...filters, counterparty: value })
            }
        },
        {
            name: t(tBase + 'site'), value: (item) => item.site,
            columnFilter: {
                value: filters.site ?? '',
                onChange: (value: string) => setFilters({ ...filters, site: value })
            }
        },
        {
            name: t(tBase + 'product'),
            value: (item) => products.find(p => p.id === item.productId)?.code ?? '',
            columnFilter: {
                value: filters.product ?? '',
                onChange: (value: string) => setFilters({ ...filters, product: value })
            }
        },
        {
            name: t(tBase + 'movementDate'), value: (item) => item.movementDate,
            columnFilter: {
                value: filters.movementDate ?? '',
                onChange: (value: string) => setFilters({ ...filters, movementDate: value })
            }
        },
        {
            name: t(tBase + 'dealType'), value: (item) => item.dealType,
            columnFilter: {
                value: filters.dealType ?? '',
                onChange: (value: string) => setFilters({ ...filters, dealType: value })
            }
        },
        {
            name: t(tBase + 'finalPricingFormulaIndex'), value: (item) => item.finalPricingFormulaIndex,
            columnFilter: {
                value: filters.finalPricingFormulaIndex ?? '',
                onChange: (value: string) => setFilters({ ...filters, finalPricingFormulaIndex: value })
            }
        },
        {
            name: t(tBase + 'validityPeriod'),
            value: (item) => item.validityPeriod ? t(`admin.masterdata.pricingPeriod.movementDate.${item.validityPeriod}`).toString() : '',
            columnFilter: {
                value: filters.validityPeriod ?? '',
                onChange: (value: string) => setFilters({ ...filters, validityPeriod: value })
            }
        },
        {
            name: t(tBase + 'finalPremium'), value: (item) => item.finalPremium,
            columnFilter: {
                value: filters.finalPremium ?? '',
                onChange: (value: string) => setFilters({ ...filters, finalPremium: value })
            }
        },
        {
            name: t(tBase + 'finalTaxes'), value: (item) => item.finalTaxes,
            columnFilter: {
                value: filters.finalTaxes ?? '',
                onChange: (value: string) => setFilters({ ...filters, finalTaxes: value })
            }
        },
        {
            name: t(tBase + 'storageFees'), value: (item) => item.storageFees,
            columnFilter: {
                value: filters.storageFees ?? '',
                onChange: (value: string) => setFilters({ ...filters, storageFees: value })
            }
        },
        {
            name: t(tBase + 'transportFees'), value: (item) => item.transportFees,
            columnFilter: {
                value: filters.transportFees ?? '',
                onChange: (value: string) => setFilters({ ...filters, transportFees: value })
            }
        },
        {
            name: t(tBase + 'quantityUnit'), value: (item) => item.quantityUnit,
            columnFilter: {
                value: filters.quantityUnit ?? '',
                onChange: (value: string) => setFilters({ ...filters, quantityUnit: value })
            }
        },
        {
            name: t(tBase + 'quantityPriceUnit'), value: (item) => item.quantityPriceUnit,
            columnFilter: {
                value: filters.quantityPriceUnit ?? '',
                onChange: (value: string) => setFilters({ ...filters, quantityPriceUnit: value })
            }
        },
        {
            name: t(tBase + 'pricingPeriod'),
            value: (item) => item.pricingPeriod ? t(`admin.masterdata.pricingPeriod.pricingFormula.${item.pricingPeriod}`).toString() : '',
            columnFilter: {
                value: filters.pricingPeriod ?? '',
                onChange: (value: string) => setFilters({ ...filters, pricingPeriod: value })
            }
        },
        {
            name: t(tBase + 'dynamicPricingPeriod'),
            value: (item) => item.pricingPeriodRuleId ? pricingPeriodRules.find(x => x.id === item.pricingPeriodRuleId)?.pricingPeriod ?? '' : '',
            hidden: !hasFeature('DynamicPricing'),
            columnFilter: {
                value: filters.dynamicPricingPeriod ?? '',
                onChange: (value: string) => setFilters({ ...filters, dynamicPricingPeriod: value })
            }
        },
        {
            name: t(tBase + 'dealDeemedDateRule'),
            value: (item) => item.dealDeemedDateRule ? t(`${tBase}dealDeemedDateRuleValue.${item.dealDeemedDateRule}`).toString() : '',
            hidden: !hasFeature('DynamicPricing'),
            columnFilter: {
                value: filters.dealDeemedDateRule ?? '',
                onChange: (value: string) => setFilters({ ...filters, dealDeemedDateRule: value })
            }
        },
        {
            name: t(tBase + 'formulaType'),
            value: (item) => item.formulaType ? t(`admin.masterdata.pricingPeriod.formulaType.${item.formulaType}`).toString() : '',
            columnFilter: {
                value: filters.formulaType ?? '',
                onChange: (value: string) => setFilters({ ...filters, formulaType: value })
            }
        },
        {
            name: t(tBase + 'sapPricingType'), value: (item) => item.sapPricingType,
            columnFilter: {
                value: filters.sapPricingType ?? '',
                onChange: (value: string) => setFilters({ ...filters, sapPricingType: value })
            }
        },
        {
            name: t(tBase + 'termSpot'), value: (item) => item.termSpot,
            columnFilter: {
                value: filters.termSpot ?? '',
                onChange: (value: string) => setFilters({ ...filters, termSpot: value })
            }
        }
    ]

    let getItems = () =>
        applyFilters(items, filters, counterpartys, products).map(toTableItem)

    let getSites = (company: string) => {
        let country = companys.find(x => x.code === company)?.country
        return sites.filter(x => x.country === country)
            .map(x => ({ value: x.code, text: x.name }))
    }

    let getDutyStatuses = (company: string) => {
        return dutyStatuss.filter(x => x.companyCode === company)
            .map(x => ({ value: x.dutyStatus, text: x.dutyStatus }))
    }

    let getDealTypes = () => {
        return Object.values(DealTypes).map(x => ({ value: x as string, text: x as string }))
    }

    let getValidityPeriods = () => {
        let validityPeriods: ValidityPeriod[] = ['FullMonth', 'Current1To10', 'Current11To20', 'Current21ToEndOfMonth']
        return validityPeriods.map(x => { return { value: x as ValidityPeriod, text: t(`admin.masterdata.pricingPeriod.movementDate.${x}`) } })
    }

    let getPricingPeriods = () => {
        let pricingFormulas: PricingFormula[] = ['FullMonth', 'Last21ToLastEndOfMonth', 'Current1To10', 'Current11To20', 'Current21ToEndOfMonth', 'DynamicPricing']
        let hasDynamicPricing = hasFeature('DynamicPricing')
        return pricingFormulas
            .filter(x => hasDynamicPricing || x !== 'DynamicPricing')
            .map(x => { return { value: x as PricingFormula, text: t(`admin.masterdata.pricingPeriod.pricingFormula.${x}`) } })
    }

    let getPricingPeriodRules = (company: string) => {
        let companyCountry: string | undefined = companys.find(x => x.code === company)?.country
        return pricingPeriodRules
        .filter(x => !companyCountry || !x.country || x.country.includes(companyCountry))
        .map(x => ({ value: x.id, text: x.pricingPeriod })) 
    }

    let getMovementDates = () => {
        let movementDates: MovementDate[] = ['1M', '11M', '21M']
        return movementDates.map(x => { return { value: x as MovementDate, text: x } })
    }

    let getSapPricingTypes = () =>
        ["Final", "Provisional"].map(x => ({ value: x, text: x }))

    let mapQuantityUnitPriceToChoice = (unitPrice: string) => ({ value: unitPrice, text: unitPrice })

    let getQuantityUnitPrices = (company: string, quantityUnit: string) => {
        let currencyLabel = 'CompanyCurrency'
        let companyCurrency = companys.find(x => x.code === company)?.companyCurrency

        if (companyCurrency)
            return quantityUnitPrices
                .filter(x => x.quantity === quantityUnit)
                .map(x => mapQuantityUnitPriceToChoice(x.unitPrice.replace(currencyLabel, companyCurrency!)))

        return quantityUnitPrices
            .filter(x => x.quantity === quantityUnit && !x.unitPrice.includes(currencyLabel))
            .map(x => mapQuantityUnitPriceToChoice(x.unitPrice))
    }

    let getQuantityUnits = () => {
        let uniqueValues = [...new Set(quantityUnitPrices.map(x => x.quantity))];
        return uniqueValues.map(x => ({ value: x, text: x }));
    }

    return (
        <div className={classes.container}>
            <MasterDataShell
                tableId="SupplyDealDefaultValues"
                headerLabel={t(tBase + 'supplyDealDefaultValues')}
                itemLabel={t(tBase + 'supplyDealDefaultValue')}
                isManager={isManager}
                columns={columns}
                items={getItems()}
                onNew={emptyItem}
                onSave={onSaveHandler}
                onDelete={onDeleteHandler}
                onExportExcel={onExportExcelHander}
            >
                {
                    (selectedItem, setSelectedItem) => (
                        <>
                            <Select label={t(tBase + 'company')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.company}
                                choices={companys.map(c => ({ value: c.code, text: c.name }))}
                                onChange={(value) => setSelectedItem({ ...selectedItem, company: value })}
                            />
                            <Select label={t(tBase + 'movementType')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.movementType}
                                choices={["Purchase", "Sale"].map(m => ({ value: m, text: m }))}
                                onChange={(value) => setSelectedItem({ ...selectedItem, movementType: value })}
                            />
                            <Select label={t(tBase + 'dutyStatus')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.dutyStatus}
                                choices={getDutyStatuses(selectedItem.company)}
                                onChange={(value) => setSelectedItem({ ...selectedItem, dutyStatus: value })}
                            />
                            <Select label={t(tBase + 'meanOfTransportation')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.meanOfTransportation}
                                choices={meanOfTransportations.map(m => ({ value: m, text: m }))}
                                onChange={(value) => setSelectedItem({ ...selectedItem, meanOfTransportation: value })}
                            />
                            <Select label={t(tBase + 'counterparty')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.counterpartyId}
                                choices={counterpartys.map(c => ({ value: c.id, text: c.name }))}
                                onChange={(value) => setSelectedItem({ ...selectedItem, counterpartyId: value })}
                            />
                            <Select label={t(tBase + 'site')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.site}
                                choices={getSites(selectedItem.company)}
                                onChange={(value) => setSelectedItem({ ...selectedItem, site: value })}
                            />
                            <Select label={t(tBase + 'product')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.productId}
                                choices={products.map(p => ({ value: p.id, text: p.code }))}
                                onChange={(value) => setSelectedItem({ ...selectedItem, productId: value })}
                            />
                            <Select label={t(tBase + 'movementDate')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.movementDate}
                                choices={getMovementDates()}
                                onChange={(value) => setSelectedItem({ ...selectedItem, movementDate: value })}
                            />
                            <Select label={t(tBase + 'dealType')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.dealType}
                                choices={getDealTypes()}
                                onChange={(value) => setSelectedItem({ ...selectedItem, dealType: value })}
                            />
                            <Select label={t(tBase + 'finalPricingFormulaIndex')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.finalPricingFormulaIndex}
                                choices={priceIndexs.map(p => ({ value: p.medecoCode, text: p.name }))}
                                onChange={(value) => setSelectedItem({ ...selectedItem, finalPricingFormulaIndex: value })}
                            />
                            <Select label={t(tBase + 'validityPeriod')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.validityPeriod}
                                choices={getValidityPeriods()}
                                onChange={validityPeriod => { setSelectedItem({ ...selectedItem, validityPeriod }) }}
                            />
                            <TextField label={t(tBase + 'finalPremium')}
                                disabled={!isManager}
                                text={selectedItem.finalPremium}
                                onChange={evt => { setSelectedItem({ ...selectedItem, finalPremium: evt.target.value }) }}
                            />
                            <TextField label={t(tBase + 'finalTaxes')}
                                disabled={!isManager}
                                text={selectedItem.finalTaxes}
                                onChange={evt => { setSelectedItem({ ...selectedItem, finalTaxes: evt.target.value }) }}
                            />
                            <TextField label={t(tBase + 'storageFees')}
                                disabled={!isManager}
                                text={selectedItem.storageFees}
                                onChange={evt => { setSelectedItem({ ...selectedItem, storageFees: evt.target.value }) }}
                            />
                            <TextField label={t(tBase + 'transportFees')}
                                disabled={!isManager}
                                text={selectedItem.transportFees}
                                onChange={evt => { setSelectedItem({ ...selectedItem, transportFees: evt.target.value }) }}
                            />
                            <Select label={t(tBase + 'quantityUnit')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.quantityUnit}
                                choices={getQuantityUnits()}
                                onChange={quantityUnit => { setSelectedItem({ ...selectedItem, quantityUnit }) }}
                            />
                            <Select label={t(tBase + 'quantityPriceUnit')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.quantityPriceUnit}
                                choices={getQuantityUnitPrices(selectedItem.company, selectedItem.quantityUnit)}
                                onChange={quantityPriceUnit => { setSelectedItem({ ...selectedItem, quantityPriceUnit }) }}
                            />
                            <Select label={t(tBase + 'pricingPeriod')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.pricingPeriod}
                                choices={getPricingPeriods()}
                                onChange={pricingPeriod => { 
                                    if (pricingPeriod !== 'DynamicPricing') {
                                        setSelectedItem({ ...selectedItem, pricingPeriod, pricingPeriodRuleId: null, dealDeemedDateRule: null })
                                    } else {
                                        setSelectedItem({ ...selectedItem, pricingPeriod })
                                    }
                                }}
                            />
                            {hasFeature('DynamicPricing') && selectedItem.pricingPeriod === 'DynamicPricing' &&
                                <Select label={t(tBase + 'dynamicPricingPeriod')}
                                    isClearable={true}
                                    clearableLabel={'-'}
                                    disabled={!isManager}
                                    value={selectedItem.pricingPeriodRuleId}
                                    choices={getPricingPeriodRules(selectedItem.company)}
                                    onChange={pricingPeriodRuleId => setSelectedItem({ ...selectedItem, pricingPeriodRuleId })} />
                            }
                            {hasFeature('DynamicPricing') &&
                                <Select label={t(tBase + 'dealDeemedDateRule')}
                                    isClearable={true}
                                    clearableLabel={'-'}
                                    disabled={!isManager}
                                    value={selectedItem.dealDeemedDateRule}
                                    choices={[{ value: 'STRUCTURETuFr', text: t(tBase + 'dealDeemedDateRuleValue.STRUCTURETuFr') }]}
                                    onChange={dealDeemedDateRule => setSelectedItem({ ...selectedItem, dealDeemedDateRule })} />
                            }
                            <Select label={t(tBase + 'formulaType')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.formulaType}
                                choices={getFormulaTypes()}
                                onChange={formulaType => { setSelectedItem({ ...selectedItem, formulaType }) }}
                            />
                            <Select label={t(tBase + 'sapPricingType')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.sapPricingType}
                                choices={getSapPricingTypes()}
                                onChange={sapPricingType => { setSelectedItem({ ...selectedItem, sapPricingType }) }}
                            />
                            <Select label={t(tBase + 'termSpot')}
                                disabled={!isManager}
                                isClearable={true}
                                clearableLabel={'-'}
                                value={selectedItem.termSpot}
                                choices={["Term", "Spot"].map(x => ({ value: x, text: x }))}
                                onChange={(value) => setSelectedItem({ ...selectedItem, termSpot: value })}
                            />
                        </>
                    )
                }
            </MasterDataShell>
        </div>
    )
}

let styles = () =>
    createStyles({
        container: { height: '100%' },
    })

type SupplyDealDefaultValueItem = {
    id: Guid
    company: string | null
    movementType: 'Purchase' | 'Sale' | null
    dutyStatus: string | null
    meanOfTransportation: string | null
    counterpartyId: string | null
    site: string | null
    productId: string | null
    movementDate: string | null
    dealType: string | null
    finalPricingFormulaIndex: string | null
    validityPeriod: string | null
    quantityUnit: string | null
    quantityPriceUnit: string | null
    pricingPeriod: string | null
    formulaType: string | null
    sapPricingType: string | null
    finalPremium: number | null
    finalTaxes: number | null
    storageFees: number | null
    transportFees: number | null
    termSpot: string | null
    pricingPeriodRuleId: string | null
    dealDeemedDateRule: string | null
}

let emptyItem = (): SupplyDealDefaultValueItem => ({
    id: guid.empty,
    company: null,
    movementType: null,
    dutyStatus: null,
    meanOfTransportation: null,
    counterpartyId: null,
    site: null,
    productId: null,
    movementDate: null,
    dealType: null,
    finalPricingFormulaIndex: null,
    validityPeriod: null,
    quantityUnit: null,
    quantityPriceUnit: null,
    pricingPeriod: null,
    formulaType: null,
    sapPricingType: null,
    finalPremium: null,
    finalTaxes: null,
    storageFees: null,
    transportFees: null,
    termSpot: null,
    pricingPeriodRuleId: null,
    dealDeemedDateRule: null
})

let toTableItem = (item: SupplyDealDefaultValueItem): TableItem<MasterDataItem<SupplyDealDefaultValueItem>> => ({
    id: item.id,
    company: item.company,
    movementType: item.movementType,
    dutyStatus: item.dutyStatus,
    meanOfTransportation: item.meanOfTransportation,
    counterpartyId: item.counterpartyId,
    site: item.site,
    productId: item.productId,
    movementDate: item.movementDate,
    dealType: item.dealType,
    finalPricingFormulaIndex: item.finalPricingFormulaIndex,
    validityPeriod: item.validityPeriod,
    quantityUnit: item.quantityUnit,
    quantityPriceUnit: item.quantityPriceUnit,
    pricingPeriod: item.pricingPeriod,
    formulaType: item.formulaType,
    sapPricingType: item.sapPricingType,
    isModified: false,
    finalPremium: item.finalPremium,
    finalTaxes: item.finalTaxes,
    storageFees: item.storageFees,
    transportFees: item.transportFees,
    termSpot: item.termSpot,
    pricingPeriodRuleId: item.pricingPeriodRuleId,
    dealDeemedDateRule: item.dealDeemedDateRule
})

let applyFilters = (items: SupplyDealDefaultValueItem[], filters: ItemFilters, counterpartys: Counterparty[], products: Product[]) => {
    return items.filter(item => {
        return Object.keys(filters).every(key => {
            let filterValue = filters[key]
            let itemValue = item[key]

            if (!filterValue) return true

            if (key.includes('counterparty')) {
                itemValue = item.counterpartyId
                let counterparty = counterpartys.find(c => c.id === itemValue)
                return counterparty ? counterparty.name.toLowerCase().includes(filterValue.toLowerCase()) : false
            }

            if (key.includes('product')) {
                itemValue = item.productId
                let product = products.find(p => p.id === itemValue)
                return product ? product.code.toLowerCase().includes(filterValue.toLowerCase()) : false
            }

            return itemValue?.toLowerCase()?.includes(filterValue.toLowerCase())
        })
    })
}

type Company = {
    code: string
    name: string
    country: string
    companyCurrency: string | null
}

type Counterparty = {
    id: Guid,
    name: string,
    companys: string[]
}

type Site = {
    code: string
    name: string
    country: string
}

type Product = {
    id: string
    code: string
    order: number
}

type DutyStatus = {
    dutyStatus: string
    companyCode: string
}

type PriceIndex = {
    name: string
    medecoCode: string
}

type QuantityUnitPrice = {
    unitPrice: string
    quantity: string
}

type PricingPeriodRule = {
    id: string
    pricingPeriod: string
    country: string[]
}

type ReferenceDto = {
    companys: Company[]
    counterpartys: Counterparty[]
    sites: Site[]
    products: Product[]
    dutyStatuss: DutyStatus[]
    priceIndexs: PriceIndex[]
    quantityUnitPrices: QuantityUnitPrice[]
    pricingPeriodRules: PricingPeriodRule[]
}

type ItemFilters = {
    company: string | null
    description: string | null
    movementType: string | null
    dutyStatus: string | null
    meanOfTransportation: string | null
    counterparty: string | null
    site: string | null
    product: string | null
    movementDate: string | null
    dealType: string | null
    finalPricingFormulaIndex: string | null
    validityPeriod: string | null
    quantityUnit: string | null
    quantityPriceUnit: string | null
    pricingPeriod: string | null
    formulaType: string | null
    sapPricingType: string | null
    finalPremium: string | null
    finalTaxes: string | null
    storageFees: string | null
    transportFees: string | null
    termSpot: string | null
    dynamicPricingPeriod: string | null
    dealDeemedDateRule: string | null
}

let noFilter: ItemFilters = {
    company: null,
    description: null,
    movementType: null,
    dutyStatus: null,
    meanOfTransportation: null,
    counterparty: null,
    site: null,
    product: null,
    movementDate: null,
    dealType: null,
    finalPricingFormulaIndex: null,
    validityPeriod: null,
    quantityPriceUnit: null,
    pricingPeriod: null,
    formulaType: null,
    sapPricingType: null,
    finalPremium: null,
    finalTaxes: null,
    storageFees: null,
    transportFees: null,
    termSpot: null,
    quantityUnit: null,
    dynamicPricingPeriod: null,
    dealDeemedDateRule: null
}

type ValidityPeriod = Extract<PricingFormula, 'FullMonth' | 'Current1To10' | 'Current11To20' | 'Current21ToEndOfMonth'>

type MovementDate = '1M' | '11M' | '21M'

export default withStyles(styles, muiOptions)(SupplyDealDefaultValue)